import { apiClient } from '@/shared/lib/axios';
import axios from 'axios';
import type {
  OperationPeriod,
  OperationPeriodsResponse,
  OperationPeriodResponse,
  GetOperationPeriodsParams,
} from '../types/operation-period.types';

/**
 * APIレスポンスの生データ型（日付がstring）
 */
interface RawOperationPeriod {
  readonly id: number;
  readonly panelId: number;
  readonly operationName: string;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly isActive: boolean;
  readonly durationMinutes: number | null;
}

interface RawOperationPeriodsResponse {
  readonly success: boolean;
  readonly operationPeriods: readonly RawOperationPeriod[];
  readonly totalCount: number;
}

interface RawOperationPeriodResponse {
  readonly success: boolean;
  readonly operationPeriod: RawOperationPeriod;
}

/**
 * 生データからドメインオブジェクトへの変換
 */
const transformOperationPeriod = (raw: RawOperationPeriod): OperationPeriod => {
  return {
    id: raw.id,
    panelId: raw.panelId,
    operationName: raw.operationName,
    startedAt: new Date(raw.startedAt),
    endedAt: raw.endedAt !== null ? new Date(raw.endedAt) : null,
    isActive: raw.isActive,
    durationMinutes: raw.durationMinutes,
  };
};

/**
 * 指定盤の運転期間一覧を取得
 */
export const fetchOperationPeriods = async (
  params: GetOperationPeriodsParams
): Promise<OperationPeriodsResponse> => {
  const { panelId, startDate, endDate } = params;

  try {
    const response = await apiClient.get<RawOperationPeriodsResponse>(
      `/api/panels/${panelId}/operation-periods`,
      {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      }
    );

    if (!response.data.success) {
      throw new Error('運転期間一覧の取得に失敗しました');
    }

    // Deep copy and transform
    const transformedPeriods = response.data.operationPeriods.map((raw) =>
      transformOperationPeriod(raw)
    );

    return {
      success: true,
      operationPeriods: transformedPeriods,
      totalCount: response.data.totalCount,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.error || '運転期間一覧の取得に失敗しました';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * 運転期間をIDで取得
 */
export const fetchOperationPeriodById = async ({
  operationPeriodId,
}: {
  operationPeriodId: number;
}): Promise<OperationPeriodResponse> => {
  try {
    const response = await apiClient.get<RawOperationPeriodResponse>(
      `/api/operation-periods/${operationPeriodId}`
    );

    if (!response.data.success) {
      throw new Error('運転期間の取得に失敗しました');
    }

    return {
      success: true,
      operationPeriod: transformOperationPeriod(response.data.operationPeriod),
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.error || '運転期間の取得に失敗しました';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * 後方互換性のためのエイリアス（段階的移行用）
 * @deprecated OperationPeriodService.fetchOperationPeriods を fetchOperationPeriods に直接置き換えてください
 */
export const OperationPeriodService = {
  fetchOperationPeriods,
  fetchOperationPeriodById: (operationPeriodId: number) =>
    fetchOperationPeriodById({ operationPeriodId }),
};
